import * as React from 'react'

import { Box, TextField, useToggle } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { EntityId, EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { DropdownDefault, DropdownOpen } from '@factorialco/f0-react/icons/app'

import Selector from 'modules/core/components/Selector'
import i18n from 'modules/core/lib/i18n'

import { SerializedTreeNode } from 'modules/job_catalog/pages/JobCatalogTreePage/types'

interface Props {
  serializedTree: string
  onLevelSelect: (value: string | undefined, roleLevelName?: string) => void
  selectedLevelId?: string
}

const JobCatalogTreeSelector = ({ serializedTree, onLevelSelect, selectedLevelId }: Props) => {
  const [selectedRoleName, setSelectedRoleName] = React.useState<string | null>(null)
  const [openRoleSelector, toggleRoleSelector] = useToggle()
  const [selectedRoleEntity, setSelectedRoleEntity] = React.useState<EntitySelectEntity | null>(
    null
  )
  const containerRef = React.useRef<HTMLDivElement>(null)

  const tree: SerializedTreeNode = React.useMemo(() => {
    const parsedTree = serializedTree ? JSON.parse(serializedTree) : []
    return Array.isArray(parsedTree) ? parsedTree[0] : parsedTree
  }, [serializedTree])

  // Find levels that belong to a specific role
  const findLevelsForRole = React.useCallback(
    (treeData: SerializedTreeNode, roleId: EntityId): SerializedTreeNode[] => {
      const findRole = (node: SerializedTreeNode): SerializedTreeNode | null => {
        if (node.node_uuid === roleId) {
          return node
        }

        if (node.children) {
          for (const child of node.children) {
            const found = findRole(child)
            if (found) return found
          }
        }

        return null
      }

      const selectedRole = findRole(treeData)

      if (!selectedRole || !selectedRole.children) {
        return []
      }

      return selectedRole.children.filter((child) => child.type === 'jobcatalog_treelevel')
    },
    []
  )

  // Find the role that contains a specific level
  const findRoleForLevel = React.useCallback(
    (treeData: SerializedTreeNode, levelId: string): SerializedTreeNode | null => {
      const findRoleContainingLevel = (node: SerializedTreeNode): SerializedTreeNode | null => {
        if (node.children) {
          for (const child of node.children) {
            if (child.type === 'jobcatalog_treelevel' && child.node_uuid === levelId) {
              return node
            }
            const found = findRoleContainingLevel(child)
            if (found) return found
          }
        }
        return null
      }

      return findRoleContainingLevel(treeData)
    },
    []
  )

  // Find the role entity based on level ID
  const findRoleEntityByLevelId = React.useCallback(
    (treeData: SerializedTreeNode, levelId: string): EntitySelectEntity | null => {
      const roleNode = findRoleForLevel(treeData, levelId)
      if (!roleNode) return null

      // Find the family and function names for this role
      let familyName: string | undefined
      let functionName: string | undefined

      const findFamilyAndFunction = (
        node: SerializedTreeNode,
        currentFamily?: string,
        currentFunction?: string
      ): void => {
        if (node.node_uuid === roleNode.node_uuid) {
          familyName = currentFamily
          functionName = currentFunction
          return
        }

        if (node.children) {
          node.children.forEach((child) => {
            if (node.type === 'jobcatalog_treefamily') {
              findFamilyAndFunction(child, node.name, currentFunction)
            } else if (node.type === 'jobcatalog_treefunction') {
              findFamilyAndFunction(child, currentFamily, node.name)
            } else {
              findFamilyAndFunction(child, currentFamily, currentFunction)
            }
          })
        }
      }

      findFamilyAndFunction(treeData)

      if (!familyName || !functionName) return null

      // Create the role entity
      return {
        id: `group_${familyName}_${functionName}`,
        name: `${familyName} | ${functionName}`,
        expanded: true,
        subItems: [
          {
            subId: roleNode.node_uuid,
            subName: roleNode.name,
            subSearchKeys: [
              roleNode.name,
              familyName,
              functionName,
              `${familyName} ${functionName}`,
              `${familyName} ${functionName} ${roleNode.name}`,
            ].filter((key): key is string => Boolean(key)),
          },
        ],
      }
    },
    [findRoleForLevel]
  )

  // Transform job catalog tree data for EntitySelect
  const transformTreeForEntitySelect = React.useCallback(
    (treeData: SerializedTreeNode): EntitySelectEntity[] => {
      if (!treeData || !treeData.children) {
        return []
      }

      // Group roles by Family + Function
      const roleGroups = new Map<string, Map<string, SerializedTreeNode[]>>()
      const allFamilyFunctionCombinations = new Set<string>()

      const groupRolesByFamilyAndFunction = (
        node: SerializedTreeNode,
        familyName?: string,
        functionName?: string
      ) => {
        // Track family-function combinations when we encounter family or function nodes
        if (node.type === 'jobcatalog_treefamily') {
          // When we encounter a family, look for functions within it
          if (node.children) {
            node.children.forEach((child) => {
              if (child.type === 'jobcatalog_treefunction') {
                allFamilyFunctionCombinations.add(`${node.name}|${child.name}`)
              }
            })
          }
        }

        if (node.type === 'jobcatalog_treerole' && familyName && functionName) {
          if (!roleGroups.has(familyName)) {
            roleGroups.set(familyName, new Map())
          }
          const familyGroup = roleGroups.get(familyName)
          if (familyGroup) {
            if (!familyGroup.has(functionName)) {
              familyGroup.set(functionName, [])
            }
            const functionGroup = familyGroup.get(functionName)
            if (functionGroup) {
              functionGroup.push(node)
            }
          }
        }

        if (node.children) {
          node.children.forEach((child) => {
            // Determine the current level
            if (node.type === 'jobcatalog_treefamily') {
              groupRolesByFamilyAndFunction(child, node.name, functionName)
            } else if (node.type === 'jobcatalog_treefunction') {
              groupRolesByFamilyAndFunction(child, familyName, node.name)
            } else {
              groupRolesByFamilyAndFunction(child, familyName, functionName)
            }
          })
        }
      }

      groupRolesByFamilyAndFunction(treeData)

      // Transform groups into EntitySelect format
      const entities: EntitySelectEntity[] = []

      // Create entities for all family-function combinations
      allFamilyFunctionCombinations.forEach((combination) => {
        const [familyName, functionName] = combination.split('|')
        if (familyName && functionName) {
          const roles = roleGroups.get(familyName)?.get(functionName) || []

          // Filter roles that have levels
          const rolesWithLevels = roles.filter((role) => {
            const roleLevels = findLevelsForRole(treeData, role.node_uuid)
            return roleLevels.length > 0
          })

          // Only create entities for groups that have roles with levels
          if (rolesWithLevels.length > 0) {
            // Create group name by combining family and function names
            const groupName = `${familyName} | ${functionName}`

            entities.push({
              id: `group_${familyName}_${functionName}`,
              name: groupName,
              expanded: true,
              subItems: rolesWithLevels.map((role) => ({
                subId: role.node_uuid,
                subName: role.name,
                subSearchKeys: [
                  role.name,
                  familyName,
                  functionName,
                  `${familyName} ${functionName}`,
                  `${familyName} ${functionName} ${role.name}`,
                ].filter((key): key is string => Boolean(key)),
              })),
            })
          }
        }
      })

      return entities
    },
    [findLevelsForRole]
  )

  const jobCatalogEntities = transformTreeForEntitySelect(tree)

  const roleEntity = React.useMemo(() => {
    if (selectedLevelId) {
      return findRoleEntityByLevelId(tree, selectedLevelId)
    }

    return selectedRoleEntity
  }, [selectedLevelId, tree, selectedRoleEntity, findRoleEntityByLevelId])

  React.useEffect(() => {
    if (roleEntity) {
      const selectedSubItem = roleEntity.subItems?.[0]
      if (selectedSubItem) {
        setSelectedRoleName(selectedSubItem.subName)
        if (
          (!selectedLevelId && roleEntity === selectedRoleEntity) ||
          (selectedLevelId && roleEntity !== selectedRoleEntity)
        ) {
          const levelsForRole = selectedSubItem.subId
            ? findLevelsForRole(tree, selectedSubItem.subId)
            : []

          if (selectedLevelId && roleEntity !== selectedRoleEntity) {
            // Find the specific level that matches selectedLevelId
            const matchingLevel = levelsForRole.find((level) => level.node_uuid === selectedLevelId)
            if (matchingLevel) {
              onLevelSelect(
                matchingLevel.node_uuid,
                `${selectedSubItem.subName} ${matchingLevel.name}`
              )
            }
          } else if (levelsForRole.length === 1 && levelsForRole[0]?.node_uuid) {
            // Auto-select the only available level
            onLevelSelect(
              levelsForRole[0].node_uuid,
              `${selectedSubItem.subName} ${levelsForRole[0].name}`
            )
          }
        }
      }
    } else {
      setSelectedRoleName(null)
    }
  }, [roleEntity, findLevelsForRole, tree, onLevelSelect, selectedLevelId, selectedRoleEntity])

  // Get levels for the selected role
  const selectedLevels = roleEntity
    ? findLevelsForRole(tree, roleEntity.subItems?.[0]?.subId ?? '')
    : []

  const levelsOptions = React.useCallback((levels: SerializedTreeNode[]) => {
    return levels.map((level) => (
      <Selector.Option
        key={level.node_uuid}
        value={level.node_uuid}
        label={level.name}
        searchValue={level.name}
      >
        {level.name}
      </Selector.Option>
    ))
  }, [])

  const handleToggleRoleSelector = React.useCallback(
    () => toggleRoleSelector(!openRoleSelector)(),
    [openRoleSelector]
  )

  return (
    <Box gap="s16" width="full" ref={containerRef} position="relative">
      <TextField
        label={i18n.t('job_catalog.job_catalog_tree_selector.role.label')}
        placeholder={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_placeholder')}
        value={selectedRoleName ?? ''}
        onChange={() => {}}
        onClick={handleToggleRoleSelector}
        missingLabel={i18n.t('common.missing_field')}
        selectValue={i18n.t('common.select')}
        showEllipsis
        indicator={<F0Icon icon={openRoleSelector ? DropdownOpen : DropdownDefault} size="sm" />}
      />
      {openRoleSelector && (
        <Box
          position="absolute"
          background="grey000White"
          borderRadius={{
            all: 'abs016',
          }}
          style={{ top: '68px', right: '0px', zIndex: '1000' }}
        >
          <EntitySelect
            label={i18n.t('job_catalog.job_catalog_tree_selector.role.label')}
            hideLabel
            singleSelector
            alwaysOpen
            width={360}
            placeholder={i18n.t('job_catalog.job_catalog_tree_selector.role.trigger_placeholder')}
            selectedItemsCopy={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.trigger_selected'
            )}
            searchPlaceholder={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.search_placeholder'
            )}
            notFoundTitle={i18n.t('job_catalog.job_catalog_tree_selector.role.not_found_title')}
            notFoundSubtitle={i18n.t(
              'job_catalog.job_catalog_tree_selector.role.not_found_subtitle'
            )}
            entities={jobCatalogEntities}
            groups={[]}
            hiddenAvatar
            selectedGroup="all"
            onSelect={(selectedEntity) => {
              if (selectedEntity === null) {
                setSelectedRoleName(null)
                setSelectedRoleEntity(null)
                onLevelSelect(undefined)
                toggleRoleSelector(false)()
              } else {
                const selectedSubItem = selectedEntity?.subItems?.[0]
                if (selectedSubItem) {
                  setSelectedRoleName(selectedSubItem.subName)
                  setSelectedRoleEntity(selectedEntity)

                  const levelsForRole = selectedSubItem.subId
                    ? findLevelsForRole(tree, selectedSubItem.subId)
                    : []

                  if (levelsForRole.length === 1 && levelsForRole[0]?.node_uuid) {
                    onLevelSelect(
                      levelsForRole[0].node_uuid,
                      `${selectedSubItem.subName} ${levelsForRole[0].name}`
                    )
                  } else {
                    onLevelSelect(undefined)
                  }

                  toggleRoleSelector(false)()
                }
              }
            }}
            onItemExpandedChange={() => {}}
            onGroupChange={() => {}}
            selectedEntities={roleEntity ? [roleEntity] : []}
          />
        </Box>
      )}
      <Selector.Popover
        label={i18n.t('job_catalog.job_catalog_tree_selector.level.label')}
        value={selectedLevelId ? [selectedLevelId] : []}
        onChange={(level) => {
          if (level) {
            setSelectedRoleEntity(null)
          }
          const selectedLevel = selectedLevels.find((l) => l.node_uuid === level)
          const levelName = selectedLevel?.name || ''
          onLevelSelect(level ? `${level}` : undefined, `${selectedRoleName} ${levelName}`.trim())
        }}
        placeholder={i18n.t('job_catalog.job_catalog_tree_selector.level.placeholder')}
        searchPlaceholder={i18n.t('job_catalog.job_catalog_tree_selector.level.search_placeholder')}
        blankLabel={i18n.t('job_catalog.job_catalog_tree_selector.level.blank_label')}
        mandatory={!!roleEntity}
        isDisabled={!roleEntity || selectedLevels.length === 1}
      >
        {levelsOptions(selectedLevels)}
      </Selector.Popover>
    </Box>
  )
}

export default JobCatalogTreeSelector
